import { useState, useEffect, useMemo, FocusEvent } from 'react';
import { ITestDefinition } from '../../models/testDefinition';
import { ITestResult } from '../../models/testResult';
import { Athlete } from './testingTypes';
import { resolveUnitLabel } from '../utils/resolveUnitLabel';

export interface EnabledTestConfig {
  testId: string;
  metrics?: string[];
  units?: string[];
  /** Label personalizzate per unità (es. { kg: "Carico gara" }) — solo presentazione */
  unitLabels?: Record<string, string>;
}

export interface PostResultBody {
  testId: string;
  athleteId: string;
  date: string;
  value?: number;
  unit?: string;
  exerciseId?: string;
  values?: Record<string, number>;
}

interface UseTestingEntryTableParams {
  selectedDate: string;
  teamId: string;
  catalog: ITestDefinition[];
  dateResultsMap: Record<string, ITestResult>;
  postResult: (body: PostResultBody) => Promise<any>;
  deleteResult: (resultId: string) => Promise<any>;
  enabledTests?: EnabledTestConfig[];
}

export function useTestingEntryTable({
  selectedDate,
  teamId,
  catalog,
  dateResultsMap,
  postResult,
  deleteResult,
  enabledTests,
}: UseTestingEntryTableParams) {
  // Stato valori cella singola (chiave: `${athleteId}_${testId}`)
  const [localValues, setLocalValues] = useState<Record<string, string>>({});
  // Stato valori multi-metrica (chiave: `${athleteId}_${testId}__${metric}`)
  const [localMetricValues, setLocalMetricValues] = useState<Record<string, string>>({});
  // Stato valori multi-unit (chiave: `${athleteId}_${testId}__unit__${unit}`)
  const [localUnitValues, setLocalUnitValues] = useState<Record<string, string>>({});
  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});

  // Reset valori locali al cambio data (sez.3.1 — nuova giornata, tabella pulita)
  useEffect(() => {
    setLocalValues({});
    setLocalMetricValues({});
    setLocalUnitValues({});
  }, [selectedDate]);

  // Mappa testId → metriche configurate (sez.2.2)
  const configMetricsByTestId = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const et of enabledTests ?? []) {
      if (et.metrics && et.metrics.length) m.set(et.testId, et.metrics);
    }
    return m;
  }, [enabledTests]);

  // Mappa testId → unitLabels configurate (per resolveUnitLabel senza prop-drilling)
  const configUnitLabelsByTestId = useMemo(() => {
    const m = new Map<string, Record<string, string>>();
    for (const et of enabledTests ?? []) {
      if (et.unitLabels && Object.keys(et.unitLabels).length) {
        m.set(et.testId, et.unitLabels);
      }
    }
    return m;
  }, [enabledTests]);

  // Mappa testId → units configurate (sez.2.1 / sez.3/6)
  const configUnitsByTestId = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const et of enabledTests ?? []) {
      if (et.units && et.units.length) m.set(et.testId, et.units);
    }
    return m;
  }, [enabledTests]);

  /**
   * Metriche da mostrare in cella: selezionate in Configura test,
   * altrimenti tutte le output se >1 (spec sez.3.2).
   */
  const getMetrics = (test: ITestDefinition): string[] => {
    const configured = configMetricsByTestId.get(test.id);
    if (configured && configured.length) return configured;
    return test.outputMetrics && test.outputMetrics.length > 1 ? test.outputMetrics : [];
  };

  /**
   * Unità affiancate da mostrare: configurate in Configura test (units[]),
   * solo se length > 1 (altrimenti la cella è single-value come ora).
   * Specifica sez.3/6.
   */
  const getUnits = (test: ITestDefinition): string[] => {
    const configured = configUnitsByTestId.get(test.id);
    if (configured && configured.length > 1) return configured;
    return [];
  };

  /**
   * Restituisce la label personalizzata per una specifica unità di un test.
   * Costruisce un mini-IEnabledTest dal testId per delegare a resolveUnitLabel.
   * Fallback: restituisce sempre l'unità grezza se la label non è configurata.
   */
  const getUnitLabel = (test: ITestDefinition, unit: string): string => {
    const labels = configUnitLabelsByTestId.get(test.id);
    const fakeEnabledTest = labels ? { testId: test.id, unit, unitLabels: labels } : undefined;
    return resolveUnitLabel(unit, fakeEnabledTest);
  };

  // ── Helpers risultato ──

  const getResult = (athleteId: string, testId: string): ITestResult | undefined =>
    dateResultsMap[`${athleteId}_${testId}`];

  const getLastResult = (athleteId: string, testId: string, historyMap: Record<string, ITestResult[]>): ITestResult | null => {
    const history = historyMap[`${athleteId}_${testId}`];
    if (!history || history.length === 0) return null;
    const withValue = history.filter((r) => r.value > 0);
    return withValue.length > 0 ? withValue[0] : null;
  };

  // ── Single-value cell ──

  const unitKey = (athleteId: string, testId: string, unit: string) =>
    `${athleteId}_${testId}__unit__${unit}`;

  const getUnitCellValue = (athleteId: string, testId: string, unit: string): string => {
    const k = unitKey(athleteId, testId, unit);
    if (k in localUnitValues) return localUnitValues[k];
    const result = getResult(athleteId, testId);
    // Mostra il valore esistente solo se la unit corrisponde (spec sez.3/6)
    if (result && result.unit === unit && result.value != null && result.value > 0) {
      return String(result.value);
    }
    return '';
  };

  const handleUnitCellChange = (athleteId: string, testId: string, unit: string, value: string) => {
    const sanitized = value.replace(/[^0-9.,]/g, '');
    setLocalUnitValues((prev) => ({ ...prev, [unitKey(athleteId, testId, unit)]: sanitized }));
  };

  const handleUnitCellBlur = async (athleteId: string, testId: string, unit: string) => {
    const k = unitKey(athleteId, testId, unit);
    const raw = localUnitValues[k];
    if (raw === undefined) return;

    const numValue = parseFloat(raw.replace(',', '.'));
    const needsSave = raw.trim() !== '' && !isNaN(numValue);
    const needsDelete = raw.trim() === '' && !!getResult(athleteId, testId)?._id;

    if (!needsSave && !needsDelete) return;

    const cellKey = `${athleteId}_${testId}__unit__${unit}`;
    setSavingCells((prev) => ({ ...prev, [cellKey]: true }));
    try {
      if (needsSave) {
        const test = catalog.find((t) => t.id === testId);
        if (test && teamId) {
          await postResult({
            testId,
            athleteId,
            date: selectedDate,
            value: numValue,
            unit,
            ...(test.exerciseId && { exerciseId: test.exerciseId }),
          });
        }
      } else if (needsDelete) {
        const existingResult = getResult(athleteId, testId);
        await deleteResult(existingResult!._id);
      }
    } catch (err) {
      console.error('Errore salvataggio risultato multi-unit:', err);
    } finally {
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    }
  };

  const getCellValue = (athleteId: string, testId: string): string => {
    const key = `${athleteId}_${testId}`;
    if (key in localValues) return localValues[key];
    const result = getResult(athleteId, testId);
    return result && result.value != null && result.value > 0 ? String(result.value) : '';
  };

  const handleCellChange = (athleteId: string, testId: string, value: string) => {
    const sanitized = value.replace(/[^0-9.,]/g, '');
    setLocalValues((prev) => ({ ...prev, [`${athleteId}_${testId}`]: sanitized }));
  };

  const handleCellBlur = async (athleteId: string, testId: string) => {
    const key = `${athleteId}_${testId}`;
    const raw = localValues[key];
    if (raw === undefined) return;

    const numValue = parseFloat(raw.replace(',', '.'));
    const needsSave = raw.trim() !== '' && !isNaN(numValue);
    const needsDelete = raw.trim() === '' && !!getResult(athleteId, testId)?._id;

    if (!needsSave && !needsDelete) return;

    setSavingCells((prev) => ({ ...prev, [key]: true }));
    try {
      if (needsSave) {
        const test = catalog.find((t) => t.id === testId);
        if (test && teamId) {
          await postResult({
            testId,
            athleteId,
            date: selectedDate,
            value: numValue,
            unit: test.defaultUnit,
            ...(test.exerciseId && { exerciseId: test.exerciseId }),
          });
        }
      } else if (needsDelete) {
        const existingResult = getResult(athleteId, testId);
        await deleteResult(existingResult!._id);
      }
    } catch (err) {
      console.error('Errore salvataggio/cancellazione risultato test:', err);
    } finally {
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // ── Multi-metrica ──

  const metricKey = (athleteId: string, testId: string, metric: string) =>
    `${athleteId}_${testId}__${metric}`;

  const getMetricValue = (athleteId: string, testId: string, metric: string): string => {
    const k = metricKey(athleteId, testId, metric);
    if (k in localMetricValues) return localMetricValues[k];
    const result = getResult(athleteId, testId);
    const v = result?.values?.[metric];
    return v != null ? String(v) : '';
  };

  const handleMetricChange = (athleteId: string, testId: string, metric: string, value: string) => {
    const sanitized = value.replace(/[^0-9.,]/g, '');
    setLocalMetricValues((prev) => ({ ...prev, [metricKey(athleteId, testId, metric)]: sanitized }));
  };

  const handleMetricBlur = async (
    athleteId: string,
    test: ITestDefinition,
    e?: FocusEvent<HTMLInputElement>,
  ) => {
    const cellKey = `${athleteId}_${test.id}`;
    // Se il focus si sposta su un altro sotto-campo della STESSA cella, non salvare
    // ancora — evita POST duplicati (dedup spec sez.3.2).
    const nextCell = (e?.relatedTarget as HTMLElement | null)?.dataset?.cell;
    if (nextCell === cellKey) return;

    const metrics = getMetrics(test);
    const values: Record<string, number> = {};
    let anyLocalTouched = false;
    for (const m of metrics) {
      const k = metricKey(athleteId, test.id, m);
      if (k in localMetricValues) anyLocalTouched = true;
      const raw = getMetricValue(athleteId, test.id, m);
      if (raw.trim() !== '') {
        const num = parseFloat(raw.replace(',', '.'));
        if (!isNaN(num)) values[m] = num;
      }
    }
    if (!anyLocalTouched) return;

    setSavingCells((prev) => ({ ...prev, [cellKey]: true }));
    try {
      if (Object.keys(values).length > 0 && teamId) {
        await postResult({
          testId: test.id,
          athleteId,
          date: selectedDate,
          values,
          ...(test.exerciseId && { exerciseId: test.exerciseId }),
        });
      } else {
        const existing = getResult(athleteId, test.id);
        if (existing?._id) await deleteResult(existing._id);
      }
    } catch (err) {
      console.error('Errore salvataggio risultato multi-metrica:', err);
    } finally {
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[cellKey];
        return next;
      });
    }
  };

  /**
   * Navigazione Enter → atleta successivo stesso test/unit (sez.3.1).
   * Cerca l'input con data-athlete e data-col corrispondenti nella riga successiva.
   */
  const handleEnterNavigateDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentAthleteId: string,
    athletes: Athlete[],
    colId: string,
  ) => {
    e.preventDefault();
    const currentIdx = athletes.findIndex((a) => a.id === currentAthleteId);
    if (currentIdx < 0 || currentIdx >= athletes.length - 1) {
      (e.target as HTMLInputElement).blur();
      return;
    }
    const nextAthleteId = athletes[currentIdx + 1].id;
    const nextInput = document.querySelector<HTMLInputElement>(
      `[data-athlete="${nextAthleteId}"][data-col="${colId}"]`,
    );
    if (nextInput) {
      nextInput.focus();
    } else {
      (e.target as HTMLInputElement).blur();
    }
  };

  return {
    localValues,
    localMetricValues,
    localUnitValues,
    savingCells,
    getMetrics,
    getUnits,
    getUnitLabel,
    getResult,
    getLastResult,
    getCellValue,
    handleCellChange,
    handleCellBlur,
    getUnitCellValue,
    handleUnitCellChange,
    handleUnitCellBlur,
    getMetricValue,
    handleMetricChange,
    handleMetricBlur,
    handleEnterNavigateDown,
    metricKey,
    unitKey,
  };
}
